import React, { useState, useEffect, useRef } from "react"
import { IoSearchOutline, IoCloseOutline } from "react-icons/io5"
import { FaCheck } from "react-icons/fa"
import { IoSettingsOutline } from "react-icons/io5"

const ColumnSelector = ({ allColumns, activeColumns, onColumnsChange }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState("")
	const [filteredColumns, setFilteredColumns] = useState(allColumns)
	const dropdownRef = useRef(null)

	useEffect(() => {
		// Filter columns based on search query
		if (searchQuery) {
			setFilteredColumns(
				allColumns.filter(col => col.name.toLowerCase().includes(searchQuery.toLowerCase()))
			)
		} else {
			setFilteredColumns(allColumns)
		}
	}, [searchQuery, allColumns])

	useEffect(() => {
		// Close dropdown when clicking outside
		const handleClickOutside = event => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false)
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () => {
			document.removeEventListener("mousedown", handleClickOutside)
		}
	}, [])

	const toggleColumn = columnId => {
		let updatedColumns

		if (activeColumns.includes(columnId)) {
			updatedColumns = activeColumns.filter(id => id !== columnId)
		} else {
			updatedColumns = [...activeColumns, columnId]
		}

		onColumnsChange(updatedColumns)
	}

	const resetToDefault = () => {
		const defaultColumns = allColumns.filter(col => col.default).map(col => col.id)

		onColumnsChange(defaultColumns)
	}

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				className="py-2 px-4 bg-white border border-gray-300 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-100 focus:outline-none"
				onClick={() => setIsOpen(!isOpen)}
			>
				<div className="flex items-center gap-1">
					<span className="inline-flex">
						<IoSettingsOutline className="h-4 w-4 text-gray-600" />
					</span>
					<span className="font-medium">Columns</span>
				</div>
			</button>

			{isOpen && (
				<div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-30 w-72 max-h-[28rem] overflow-hidden flex flex-col">
					<div className="p-3 border-b border-gray-200">
						<div className="font-semibold text-gray-700 mb-2">Customize Columns</div>
						<div className="relative">
							<IoSearchOutline className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
							<input
								type="text"
								placeholder="Search columns..."
								className="w-full py-2 pl-8 pr-8 rounded-md bg-gray-100 border-transparent focus:border-customBlue focus:bg-white focus:ring-0 text-sm h-8 text-gray-600 placeholder-gray-500 outline-none"
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
							/>
							{searchQuery && (
								<button
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
									onClick={() => setSearchQuery("")}
								>
									<IoCloseOutline className="h-5 w-5" />
								</button>
							)}
						</div>
					</div>

					<div className="overflow-y-auto flex-1 max-h-80">
						{filteredColumns.length === 0 ? (
							<div className="p-4 text-center text-gray-500">
								No columns match your search
							</div>
						) : (
							filteredColumns.map(column => (
								<div
									key={column.id}
									className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
									onClick={() => toggleColumn(column.id)}
								>
									<span className="text-gray-700">{column.name}</span>
									<div
										className={`w-5 h-5 rounded flex items-center justify-center ${activeColumns.includes(column.id) ? "bg-customBlue" : "border border-gray-300"}`}
									>
										{activeColumns.includes(column.id) && (
											<FaCheck className="text-white text-xs" />
										)}
									</div>
								</div>
							))
						)}
					</div>

					<div className="p-3 border-t border-gray-200 flex justify-between">
						<button
							className="text-customBlue text-sm hover:underline"
							onClick={resetToDefault}
						>
							Reset to Default
						</button>
						<button
							className="bg-customBlue text-white px-3 py-1 rounded-md text-sm hover:bg-customBlue-dark"
							onClick={() => setIsOpen(false)}
						>
							Done
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

export default ColumnSelector
